import { useMemo } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFacebook, faGoogle } from '@fortawesome/free-brands-svg-icons'
import { faHome } from '@fortawesome/pro-solid-svg-icons'

import {
  StatusBadge, Table, Text,
} from '@domain-group/suit-theme'

import { findLeastProgressedStatus, getColour, getIcon } from '@functions/campaignSummary'
import formatDate from '@functions/utils/formatDate'

import useCampaigns from '@hooks/useCampaigns'

import { CampaignModel } from '@models/types'

import Actions from './Actions'

const buildColumns = () => [
  {
    title: 'Campaign',
    dataKey: 'name',
    cellProps: {
      maxWidth: 250,
      verticalAlign: 'middle',
      width: 250,
    },
    render: (name: string) => (
      <Text variant="ellipsis" width={250}>
        {name}
      </Text>
    ),
  },
  {
    title: 'AIM#',
    dataKey: 'id',
    cellProps: {
      textAlign: 'center',
      verticalAlign: 'middle',
      width: 40,
    },
  },
  {
    title: 'Due',
    dataKey: 'requiredAt',
    cellProps: {
      textAlign: 'center',
      verticalAlign: 'middle',
      width: 40,
    },
    render: (dueDate: string) => (
      <StatusBadge
        boxProps={{
          backgroundColor: 'backgroundColor',
          borderColor: 'lineColor',
          color: 'black',
        }}
        text={formatDate(dueDate, 'ISO8601', 'dd/MM/yyyy')}
      />
    ),
  },
  {
    title: <FontAwesomeIcon icon={faHome} />,
    dataKey: 'campaignSummary.preflightValidation',
    cellProps: {
      key: 'campaignStatus',
      textAlign: 'center',
      verticalAlign: 'middle',
      width: 20,
    },
    render: (summary: CampaignModel['campaignSummary']['preflightValidation']) => {
      const status = findLeastProgressedStatus([summary?.campaignStatus, summary?.productStatus])

      return (
        <FontAwesomeIcon
          color={getColour({ status })}
          icon={getIcon({ status })}
          size="lg"
        />
      )
    },
  },
  {
    title: <FontAwesomeIcon icon={faFacebook} />,
    dataKey: 'campaignSummary.preflightValidation',
    cellProps: {
      key: 'facebookStatus',
      textAlign: 'center',
      verticalAlign: 'middle',
      width: 20,
    },
    render: (summary: CampaignModel['campaignSummary']['preflightValidation']) => (
      <FontAwesomeIcon
        color={getColour({ status: summary?.facebookStatus })}
        icon={getIcon({ status: summary?.facebookStatus })}
        size="lg"
      />
    ),
  },
  {
    title: <FontAwesomeIcon icon={faGoogle} />,
    dataKey: 'campaignSummary.preflightValidation',
    cellProps: {
      key: 'googleStatus',
      textAlign: 'center',
      verticalAlign: 'middle',
      width: 20,
    },
    render: (summary: CampaignModel['campaignSummary']['preflightValidation']) => (
      <FontAwesomeIcon
        color={getColour({ status: summary?.googleStatus })}
        icon={getIcon({ status: summary?.googleStatus })}
        size="lg"
      />
    ),
  },
  {
    title: 'Actions',
    dataKey: 'id',
    cellProps: {
      key: 'actions',
      textAlign: 'center',
      verticalAlign: 'middle',
      width: 20,
    },
    render: (campaignId: number) => <Actions campaignId={campaignId} />,
  },
]

interface OrderValidationTableProps {
  campaignsPayload: ReturnType<typeof useCampaigns>,
}

const OrderValidationTable = (props: OrderValidationTableProps) => {
  const {
    campaignsPayload: {
      filteredCampaigns,
      filteredCampaignsCount,
    },
  } = props

  const memoColumns = useMemo(() => buildColumns(), [filteredCampaignsCount])

  return (
    <Table
      boxProps={{ border: 'none', maxHeight: 1600, overflowY: 'auto' }}
      columns={memoColumns}
      data={filteredCampaigns}
      scroll={{ x: 800 }}
      stickyHeader
    />
  )
}

export default OrderValidationTable
